import React from 'react';
import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';
import toast from 'react-hot-toast';
import { usePicks } from '../../context/PicksContext';
import { Product, ProductVariant, PickSelections } from '../../types';
import { MAX_PICKS } from '../../utils/picksShare';

interface Props {
  product: Product | null;
  variant: ProductVariant | null;        // may be null (bare pick)
  selections: PickSelections | null;     // may have 0 or more filled fields
  /** Explicitly disable (e.g. when product hasn't loaded yet). */
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  lineName?: string;
  /** Prevent bubbling when embedded inside a clickable card. */
  stopPropagation?: boolean;
}

/**
 * Heart toggle for "My Picks". Works with any level of configuration:
 * - Bare (no selections) → saves the product for later
 * - Partial (just color, or color+size) → saves what's configured
 * - Complete (all selections) → saves ready-for-cart pick
 */
export function SaveToPicksButton({
  product, variant, selections, disabled, className, size = 'md', lineName, stopPropagation,
}: Props) {
  const { togglePick, hasPick, totalPicks } = usePicks();

  const effSelections: PickSelections = selections || {};
  const isSaved = product ? hasPick(product.productId, effSelections) : false;
  const isFull = totalPicks >= MAX_PICKS;
  const effDisabled = disabled || !product || (!isSaved && isFull);

  const dim = size === 'lg' ? 'w-14 h-14' : size === 'sm' ? 'w-9 h-9' : 'w-12 h-12';
  const iconSize = size === 'lg' ? 22 : size === 'sm' ? 16 : 20;

  const handleClick = (e: React.MouseEvent) => {
    if (stopPropagation) { e.stopPropagation(); e.preventDefault(); }
    if (effDisabled || !product) return;
    const result = togglePick(product, variant, effSelections);
    const label = lineName || product.name;
    if (result === 'added') {
      toast.success(`${label} saved to My Picks`, { icon: '♥' });
    } else if (result === 'removed') {
      toast(`${label} removed from My Picks`, { icon: '♡' });
    } else if (result === 'full') {
      toast.error(`My Picks is full (${MAX_PICKS} max). Remove one to add another.`);
    }
  };

  const title = isSaved
    ? 'Remove from My Picks'
    : isFull
    ? `My Picks is full (${MAX_PICKS} max)`
    : 'Save to My Picks';

  return (
    <motion.button
      type="button"
      whileTap={effDisabled ? undefined : { scale: 0.92 }}
      onClick={handleClick}
      disabled={effDisabled}
      aria-label={title}
      title={title}
      className={
        `${dim} flex-shrink-0 rounded-2xl flex items-center justify-center border-2 transition-all duration-200 ` +
        (effDisabled
          ? 'border-gray-200 text-gray-300 bg-white cursor-not-allowed'
          : isSaved
          ? 'border-[#7B68CC] text-[#7B68CC] bg-[#7B68CC]/10 hover:bg-[#7B68CC]/15'
          : 'border-gray-200 text-gray-500 bg-white hover:border-[#7B68CC] hover:text-[#7B68CC]'
        ) +
        (className ? ` ${className}` : '')
      }
    >
      <Heart size={iconSize} fill={isSaved ? 'currentColor' : 'none'} />
    </motion.button>
  );
}
